import { parseISO, isValid } from 'date-fns';

import { TIMEZONE_ABBREVIATION_OFFSETS } from './timezoneAbbreviationOffsets';

const API_DATE_TIME_REGEX =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AP]M)\b)?)?(?:\s*([A-Z]{1,5}|(?:GMT|UTC|UT)?[+-]\d{1,2}(?::?\d{2})?))?$/i;
const NUMERIC_OFFSET_REGEX = /^(?:GMT|UTC|UT)?([+-])(\d{1,2})(?::?(\d{2}))?$/i;
const MAX_OFFSET_HOURS = 14;

const pad = (value: string | number) => String(value).padStart(2, '0');

function resolveTimezoneOffset(zone: string): string | null {
    const numericOffset = zone.match(NUMERIC_OFFSET_REGEX);
    if (numericOffset) {
        const [, sign = '+', hours = '', minutes = '00'] = numericOffset;
        return Number(hours) <= MAX_OFFSET_HOURS && Number(minutes) < 60 ? `${sign}${pad(hours)}:${minutes}` : null;
    }
    return TIMEZONE_ABBREVIATION_OFFSETS[zone.toUpperCase()] ?? '+00:00';
}

function parseApiDateTime(match: RegExpMatchArray): Date | null {
    const [, month = '', day = '', year = '', hours = '0', minutes = '0', seconds = '0', meridiem = '', zone = ''] =
        match;
    const offset = resolveTimezoneOffset(zone);
    let hour = Number(hours);
    if (meridiem) {
        if (hour < 1 || hour > 12) return null;
        hour = (hour % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0);
    }
    if (!offset) return null;
    const date = parseISO(`${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minutes)}:${pad(seconds)}${offset}`);
    return isValid(date) ? date : null;
}

export function parseDateValue(
    rawDate: unknown,
    { preserveTime = false }: { preserveTime?: boolean } = {},
): Date | null {
    if (!rawDate) {
        return null;
    }

    if (rawDate instanceof Date) {
        return isValid(rawDate) ? rawDate : null;
    }

    if (typeof rawDate === 'number') {
        const date = new Date(rawDate);
        return isValid(date) ? date : null;
    }

    if (typeof rawDate === 'string') {
        // Check for ISO
        let date = parseISO(rawDate);
        if (isValid(date)) {
            return date;
        }

        if (preserveTime) {
            const apiMatch = rawDate.trim().match(API_DATE_TIME_REGEX);
            if (apiMatch) return parseApiDateTime(apiMatch);
        } else {
            const match = rawDate.match(/^(\d{1,2}\/\d{1,2}\/\d{4})/);
            if (match && match[1]) {
                const [month = '', day = '', year = ''] = match[1].split('/');

                if (month && day && year) {
                    // Construct a predictable ISO string and parse it.
                    const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
                    date = parseISO(isoString);
                    if (isValid(date)) {
                        return date;
                    }
                }
            }
        }

        date = new Date(rawDate);
        if (isValid(date)) {
            return date;
        }
    }

    // All parsing attempts failed
    return null;
}
